import type { ActivityItem } from '../activity-journal';
import { stableActivityBuildingPublicId } from '../activity-journal/job';

export interface ActivityBuildingDisplay {
    readonly details: ActivityBuildingDetails;
    readonly location: ActivityBuildingLocation | null;
    readonly label: string;
    readonly address: string | null;
}

export interface ActivityBuildingDetails {
    readonly name: string;
    readonly address: string;
}

export interface ActivityBuildingLocation {
    readonly buildingCity: string;
}

export interface ActivityBuildingContext {
    readonly buildingPublicId: string | null;
    readonly details: ActivityBuildingDetails | null;
    readonly location: ActivityBuildingLocation | null;
    readonly label: string;
    readonly address: string | null;
}

export function activityBuildingContext(
    item: ActivityItem,
    buildingLookups: ReadonlyMap<string, ActivityBuildingDisplay>
): ActivityBuildingContext {
    const buildingPublicId = item.identityQuarantined
        ? null
        : stableActivityBuildingPublicId(item.jobs);
    const resolved = buildingPublicId
        ? buildingLookups.get(buildingPublicId)
        : undefined;

    if (resolved) return { buildingPublicId, ...resolved };

    const acceptedName = item.identityQuarantined
        ? null
        : stableAcceptedBuildingName(item);

    return {
        buildingPublicId,
        details: null,
        location: null,
        label:
            acceptedName ??
            (buildingPublicId
                ? unresolvedActivityBuildingLabel(buildingPublicId)
                : 'Unidentified building'),
        address: null,
    };
}

function stableAcceptedBuildingName(item: ActivityItem): string | null {
    const names = [
        ...new Set(
            item.jobs.flatMap((job) => {
                const name = job.buildingName?.trim();
                return name ? [name] : [];
            })
        ),
    ];
    return names.length === 1 ? (names[0] ?? null) : null;
}

export function unresolvedActivityBuildingLabel(
    buildingPublicId: string
): string {
    return `Building ${compactActivityReference(buildingPublicId)}`;
}

export function compactActivityReference(value: string): string {
    return value.length <= 12 ? value : `…${value.slice(-8)}`;
}
